import {
  Grid,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  tableRowClasses,
} from '@mui/material';
import React, { useState } from 'react';
import { RootineMobileTable } from './RootineMobileTable';
import { ColumnProps, RootineTableColumn } from './RootineTableColumn';
import { RootineTableRow, RowProps } from './RootineTableRow';

export interface TableProps {
  tableName: string;
  columns: { [name: string]: ColumnProps };
  rows?: { [name: string]: RowProps }[];
  primaryColor?: string;
  secondaryColor?: string;
  selectedColor?: string;
  maxHeight?: number;
  maxHeightMobile?: number;
  onClick?: (index: number) => void;
  forceMobile?: boolean;
}

export const RootineTable = (props: TableProps): JSX.Element => {
  const [selectedRowId, setSelectedRowId] = useState<number>();
  const handleClick = (idx: number) => {
    if (props.onClick) {
      props.onClick(idx);
    }
  };

  return (
    <Grid container>
      {!props.forceMobile && (
        <>
          <Grid item md={12} sx={{ display: { xs: 'none', md: 'inline' } }}>
            <TableContainer
              sx={{
                backgroundColor: props.primaryColor || 'white',
                overflow: 'scroll-y',
                overflowX: 'auto',
                maxHeight: props.maxHeight,
                '&::-webkit-scrollbar': { width: 5 },
                '&::-webkit-scrollbar-track': { background: '#F8F8F8' },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: 5,
                },
              }}
            >
              <Table
                stickyHeader
                sx={{
                  minWidth: 650,
                  [`& .${tableCellClasses.root}`]: {
                    borderBottomColor: props.secondaryColor || '#F8F8F8',
                    borderBottomWidth: 10,
                  },
                  tableLayout: 'fixed',
                }}
                aria-label={props.tableName}
              >
                <TableHead>
                  <TableRow>
                    {Object.values(props.columns).map(
                      (column: ColumnProps, i: number) => {
                        return (
                          <TableCell
                            key={i}
                            align={column.center ? 'center' : 'inherit'}
                            sx={{
                              backgroundColor:
                                'theme.palette.background.default',
                              border: 'none',
                            }}
                          >
                            <RootineTableColumn {...column} />
                          </TableCell>
                        );
                      }
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props.rows &&
                    props.rows.map(
                      (row: { [name: string]: RowProps }, i: number) => {
                        return (
                          <TableRow
                            key={i}
                            hover
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                              cursor: props.onClick ? 'pointer' : '',
                              [`&.${tableRowClasses.selected}`]: {
                                backgroundColor:
                                  props.selectedColor || '#F0F2F9',
                              },
                            }}
                            onClick={(e) => {
                              handleClick(i);
                              setSelectedRowId(i);
                            }}
                            selected={selectedRowId === i}
                          >
                            {Object.keys(props.columns).map(
                              (field: string, i: number) => {
                                return (
                                  <TableCell
                                    key={`${i}-` + field}
                                    component="th"
                                    scope="row"
                                    align={
                                      row[field].center ? 'center' : 'inherit'
                                    }
                                  >
                                    <RootineTableRow {...row[field]} />
                                  </TableCell>
                                );
                              }
                            )}
                          </TableRow>
                        );
                      }
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} sx={{ display: { xs: 'inline', md: 'none' } }}>
            <RootineMobileTable {...props} />
          </Grid>
        </>
      )}
      {props.forceMobile && (
        <Grid item xs={12} sx={{ display: { xs: 'inline' } }}>
          <RootineMobileTable {...props} />
        </Grid>
      )}
    </Grid>
  );
};
